import { MetricValue } from '../MetricValue/MetricValue'
import { useMetricSnapshots } from 'hooks/queries/useMetricSnapshots'
import { Period } from 'screens/Analytics/constants'
import useURLParams from 'hooks/useURLParams'
import { MetricSnapshot } from 'hooks/queries/externalIntegrations/types'
import { MetricContainer } from '../MetricContainer/MetricContainer'
import BarChart from '../BarChart/BarChart'
import Styles from './Metric.module.scss'
interface MetricProps {
  name: string
  metricName: string
  tooltip: string
  period: Period
  showLastSnapshotAsTotal?: boolean
}

export const getRate = (diff: number) => {
  if (diff === 0) return 'neutral'
  return diff > 0 ? 'positive' : 'negative'
}

const sumMetricValues = (snapshots: MetricSnapshot[]) =>
  snapshots.reduce((acc, snapshot) => acc + snapshot.metric_value, 0)

export const Metric = ({ name, metricName, period, tooltip, showLastSnapshotAsTotal = false }: MetricProps) => {
  const [searchParams] = useURLParams()

  const { data } = useMetricSnapshots({
    metricName,
    period,
    externalIntegrationId: parseInt(searchParams.get('external_integration_id') as string),
  })

  const snapshots = data?.metric_snapshots || []

  if (snapshots.length < 3) {
    return (
      <MetricContainer
        name={name}
        tooltip={tooltip}
        headerContent={<MetricValue name="Total" value={null} type="absolute" />}
      >
        <BarChart data={[]} height={200} name={name} period={period} />
      </MetricContainer>
    )
  }

  const firstSnapshot = snapshots[0]
  const latestSnapshot = snapshots[snapshots.length - 1]

  const absoluteDiff = latestSnapshot.metric_value - firstSnapshot.metric_value
  const percentageDiff =
    absoluteDiff === 0 && firstSnapshot.metric_value === 0 ? 0 : (absoluteDiff / firstSnapshot.metric_value) * 100
  const total = showLastSnapshotAsTotal ? latestSnapshot.metric_value : sumMetricValues(snapshots)

  return (
    <MetricContainer
      name={name}
      tooltip={tooltip}
      headerContent={
        <div className={Styles.metricValues}>
          <MetricValue name="Total" value={total} type="absolute" />
          <MetricValue name="Growth" value={absoluteDiff} rate={getRate(absoluteDiff)} type="absolute" />
          <MetricValue
            name="Rate"
            value={Math.round(percentageDiff)}
            rate={getRate(percentageDiff)}
            type="percentage"
          />
        </div>
      }
    >
      <BarChart data={snapshots} height={200} name={name} period={period} />
    </MetricContainer>
  )
}
